import { prepareWindowVariable } from '../helpers';
import { CLIENT_TYPES } from './ClientTypes';

declare global {
  interface IEchoTex {
    clients: {
      [key: string]: any;
    };
  }
  interface Window {
    echotex: IEchoTex;
  }
}

class ApiClientManager {
  private clients: Map<CLIENT_TYPES, any> = new Map();

  constructor() {
    // Create an instance and save the promise returning it in a window variable
    // Why the promise? Because multiple callers will end up waiting for the same promise to get finished
    // and will eventually get the same instance of the client.
    // Why the window variable? So all apps will use the same instance and share the same buffer
    prepareWindowVariable();
  }

  /**
   * Creates or retrieves an instance of the API client
   */

  private async initializeApiClient<T>(clientKey: CLIENT_TYPES): Promise<T> {
    switch (clientKey) {
      case CLIENT_TYPES.AUTH_CLIENT: {
        const { EchoTexAuthClientV1 } = await import('@echotex/api-client-auth');

        return new EchoTexAuthClientV1() as T;
      }

      case CLIENT_TYPES.NAVIGATION_CLIENT: {
        const { EchoTexNavigationClientV1 } = await import('@echotex/api-client-navigation');

        return new EchoTexNavigationClientV1() as T;
      }

      case CLIENT_TYPES.DYNAMIC_MENU_CLIENT: {
        const { EchoTexDynamicMenuClientV1 } = await import('@echotex/api-client-dynamicMenu');

        return new EchoTexDynamicMenuClientV1() as T;
      }

      case CLIENT_TYPES.REPORT_CLIENT: {
        const { EchoTexReportClientV1 } = await import('@echotex/api-client-report');

        return new EchoTexReportClientV1() as T;
      }

      case CLIENT_TYPES.NOTIFICATION_CLIENT: {
        const { EchoTexNotificationClientV1 } = await import('@echotex/api-client-notification');

        return new EchoTexNotificationClientV1() as T;
      }

      case CLIENT_TYPES.ROLE_BASED_ACCESS_CLIENT: {
        const { EchoTexRoleBasedAccessClientV1 } = await import('@echotex/api-role-based-access');

        return new EchoTexRoleBasedAccessClientV1() as T;
      }

      case CLIENT_TYPES.MERCHANDISING_CLIENT: {
        const { EchoTexMerchandisingClientV1 } = await import('@echotex/api-merchandising');

        return new EchoTexMerchandisingClientV1() as T;
      }

      case CLIENT_TYPES.SCM_CLIENT: {
        const { EchoTexScmClientV1 } = await import('@echotex/api-scm');

        return new EchoTexScmClientV1() as T;
      }

      case CLIENT_TYPES.ACCESS_CONTROL_MANAGEMENT: {
        const { EchoTexAccessControlManagementClientV1 } = await import('@echotex/api-access-control-management');

        return new EchoTexAccessControlManagementClientV1() as T;
      }

      case CLIENT_TYPES.FEEDBACK_AND_SUGGESTION: {
        const { EchoTexFeedbackAndSuggestionClientV1 } = await import('@echotex/api-feedback-and-suggestion');

        return new EchoTexFeedbackAndSuggestionClientV1() as T;
      }

      case CLIENT_TYPES.HELP_AND_SUPPORT: {
        const { EchoTexHelpAndSupportClientV1 } = await import('@echotex/api-help-and-support');

        return new EchoTexHelpAndSupportClientV1() as T;
      }

      case CLIENT_TYPES.SUPPLIER_CLIENT: {
        const { EchoTexSupplierClientV1 } = await import('@echotex/api-client-supplier');

        return new EchoTexSupplierClientV1() as T;
      }

      case CLIENT_TYPES.HRMS_CLIENT: {
        const { EchoTexHrmsClientV1 } = await import('@echotex/api-client-hrms');

        return new EchoTexHrmsClientV1() as T;
      }

      case CLIENT_TYPES.UTIL_CLIENT: {
        const { EchoTexUtilClientV1 } = await import('@echotex/api-client-utility');

        return new EchoTexUtilClientV1() as T;
      }

      case CLIENT_TYPES.BOOKING_CLIENT: {
        const { EchoTexBookingClientV1 } = await import('@echotex/api-booking');

        return new EchoTexBookingClientV1() as T;
      }

      default:
        return null as T;
    }
  }

  /**
   * Creates or retrieves an instance of the API client
   */
  async getClient<T>(clientKey: CLIENT_TYPES): Promise<T> {
    // If we already have an instance (or a promise to make one), just return it
    if (this.clients.has(clientKey)) {
      return this.clients.get(clientKey);
    }

    const client = await this.initializeApiClient<T>(clientKey);
    this.clients.set(clientKey, client);

    // 🔓 In dev only, expose for debugging
    if (process.env.NODE_ENV === 'development') {
      if (!window?.echotex?.clients?.[clientKey]) {
        window.echotex.clients[clientKey] = client;
      }
    }

    return client;
  }
}

export const ApiClient = new ApiClientManager();
